import { Collection, FamilyBar, Help, NotificationEntry, PageHeader, Sidebar, Unavailable, productNames, useConfirm, useToaster, type Copy, type FamilyProductId } from '@beyond-js/ui/react';
import type { Language } from '@snapshots/locale';

type Text = (key: string, values?: Record<string, string | number>) => string;

/**
 * The shared components' copy in the person's language. Since `@beyond-js/ui` 0.7.2 every component
 * ships its words in English and Spanish (`X.labels.en`, `X.labels.es`), so the family's words come
 * from there; this application's catalogs add only what is its own (its name, its noun "captures").
 */
export class Copies {
	/** The notification entry's and inbox's words: one set for both, entirely the family's */
	static notifications(language: Language): Copy {
		return NotificationEntry.labels[language];
	}

	/**
	 * The family bar's words; product names stay untranslated. The bar's header names this product, and
	 * the sign-out entry says only "Sign out" when no descriptor names Accounts' `/leave` (`beyond`, Q09)
	 */
	static family(language: Language, text: Text, beyond = false): Copy {
		const copy: Copy = { ...FamilyBar.labels[language], header: text('app.name') };
		if (!beyond) copy.signout = text('family.signout');
		return copy;
	}

	/** A role's name in the person's language, as the family bar says it */
	static role(language: Language, role: string): string {
		const name = FamilyBar.labels[language].role;
		return typeof name === 'function' ? name({ role }) : role;
	}

	/** The words of this product's sidebar (D53): its drawer's name below the cut and its Close */
	static sidebar(language: Language): Copy {
		return Sidebar.labels[language];
	}

	static #collection = ['search', 'placeholder', 'all', 'loading', 'failure', 'pages'];
	static #counts = { label: '{label}' };

	/**
	 * The shared collection's words, with this list's own where they name captures: what the search
	 * looks in, loading and failing to load them, "all" agreeing with the noun, and no matches said
	 * apart from nothing at all
	 */
	static collection(language: Language, text: Text): Copy {
		const copy: Copy = { ...Collection.labels[language], ...Object.fromEntries(Copies.#collection.map(key => [key, text(`list.${key}`, Copies.#counts)])) };
		copy.matches = ({ query }) => (query ? text('list.matches', { query: String(query) }) : text('list.filtered'));
		return copy;
	}

	static help(language: Language): Copy {
		return Help.labels[language];
	}

	/** A page header's words: the crumbs' name */
	static header(language: Language): Copy {
		return PageHeader.labels[language];
	}

	/** "Who can change this:" before the owner of a refusal in place (D06) */
	static unavailable(language: Language): Copy {
		return Unavailable.labels[language];
	}

	/** A question's words: Cancel, the consequence's terms (D17) and the failure line */
	static questions(language: Language): Copy {
		return useConfirm.labels[language];
	}

	static toasts(language: Language): Copy {
		return useToaster.labels[language];
	}

	/** The products an inbox may hold, by the family's display names (`productNames`, Packages among them since `@beyond-js/ui` 0.15.0) */
	static products: Record<string, string> = Object.fromEntries((['snapshots', 'projects', 'packages', 'workspace', 'delegate', 'cdn', 'conduict', 'accounts'] satisfies FamilyProductId[]).map(id => [id, productNames[id]]));
}
